import { useSyncExternalStore } from 'react';

// Sound settings, saved on this device (no account). Two separate switches:
// - ambient: the background music loops (site ambient and Let Go)
// - notifications: the short chimes below (someone joined, new message, thought posted)
// Turning one off never touches the other.
export type SoundPrefs = { ambient: boolean; notifications: boolean };
const SOUND_KEY = 'zuzu_sound';
const DEFAULT_PREFS: SoundPrefs = { ambient: true, notifications: true };

function loadPrefs(): SoundPrefs {
  try {
    const saved = JSON.parse(localStorage.getItem(SOUND_KEY) || 'null');
    if (saved && typeof saved === 'object') {
      return { ambient: saved.ambient !== false, notifications: saved.notifications !== false };
    }
  } catch { /* private mode or bad data: use the defaults */ }
  return DEFAULT_PREFS;
}

let prefs: SoundPrefs = typeof window === 'undefined' ? DEFAULT_PREFS : loadPrefs();
const listeners = new Set<() => void>();

export const getSoundPrefs = () => prefs;
export function setSoundPrefs(patch: Partial<SoundPrefs>) {
  const wasOff = !prefs.notifications;
  prefs = { ...prefs, ...patch };
  try { localStorage.setItem(SOUND_KEY, JSON.stringify(prefs)); } catch { /* not saved, still works now */ }
  listeners.forEach(fn => fn());
  // Notification sounds just turned on: play the soft new-message chime once, so it's
  // clear they work. Only the sound; no message or notification is involved.
  if (wasOff && prefs.notifications) playMessageReceived();
}
export function subscribeSoundPrefs(fn: () => void) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
export const useSoundPrefs = () => useSyncExternalStore(subscribeSoundPrefs, getSoundPrefs, () => DEFAULT_PREFS);

// Something else is playing its own sound for a moment (the breathing music or a
// video on World Mental Health Day): the ambient music pauses, without changing the
// saved switch. Each source holds by its own name, so one cannot release another.
const holders = new Set<string>();
let ambientHeld = false;
const holdListeners = new Set<() => void>();
export function holdAmbient(who: string, on: boolean) {
  if (on) holders.add(who); else holders.delete(who);
  const next = holders.size > 0;
  if (ambientHeld === next) return;
  ambientHeld = next;
  holdListeners.forEach(fn => fn());
}
const subscribeHold = (fn: () => void) => { holdListeners.add(fn); return () => { holdListeners.delete(fn); }; };
export const useAmbientHeld = () => useSyncExternalStore(subscribeHold, () => ambientHeld, () => false);

const chimesOff = () => !prefs.notifications;

const ctx = () => new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();

export function playChatConnected() {
  if (chimesOff()) return;
  try {
    const ac = ctx();
    [{ freq: 440, start: 0 }, { freq: 554, start: 0.13 }].forEach(({ freq, start }) => {
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      osc.connect(gain);
      gain.connect(ac.destination);
      osc.type = 'triangle';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, ac.currentTime + start);
      gain.gain.linearRampToValueAtTime(0.35, ac.currentTime + start + 0.008);
      gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + start + 0.3);
      osc.start(ac.currentTime + start);
      osc.stop(ac.currentTime + start + 0.31);
    });
  } catch { /* no audio on this device */ }
}

export function playMessageReceived() {
  if (chimesOff()) return;
  try {
    const ac = ctx();
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.connect(gain);
    gain.connect(ac.destination);
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0, ac.currentTime);
    gain.gain.linearRampToValueAtTime(0.12, ac.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.2);
    osc.start(ac.currentTime);
    osc.stop(ac.currentTime + 0.21);
  } catch { /* no audio on this device */ }
}

export function playThoughtPosted() {
  if (chimesOff()) return;
  try {
    const ac = ctx();
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.connect(gain);
    gain.connect(ac.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(220, ac.currentTime);
    osc.frequency.linearRampToValueAtTime(550, ac.currentTime + 0.4);
    gain.gain.setValueAtTime(0, ac.currentTime);
    gain.gain.linearRampToValueAtTime(0.2, ac.currentTime + 0.08);
    gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.9);
    osc.start(ac.currentTime);
    osc.stop(ac.currentTime + 0.91);
  } catch { /* no audio on this device */ }
}
